import { sanitizeResellerCode } from './resellerRecords';

export const RESELLER_REFERRAL_STORAGE_KEY = 'trader_journal_reseller_referral_v1';
export const RESELLER_REFERRAL_TTL_DAYS = 90;

const DAY_MS = 24 * 60 * 60 * 1000;

const safeParseJson = (value) => {
    try {
        return value ? JSON.parse(value) : null;
    } catch (_error) {
        return null;
    }
};

const getStorage = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        return window.localStorage || null;
    } catch (_error) {
        return null;
    }
};

const getCurrentHref = () => (
    typeof window !== 'undefined' && window.location?.href
        ? window.location.href
        : ''
);

export const isReferralAttributionExpired = (attribution, now = Date.now()) => {
    const capturedAt = Date.parse(attribution?.capturedAt || '');
    if (!Number.isFinite(capturedAt)) {
        return true;
    }

    return now - capturedAt > RESELLER_REFERRAL_TTL_DAYS * DAY_MS;
};

export const readReferralAttribution = ({ now = Date.now() } = {}) => {
    const storage = getStorage();
    if (!storage) {
        return null;
    }

    const attribution = safeParseJson(storage.getItem(RESELLER_REFERRAL_STORAGE_KEY));
    if (!attribution?.code || isReferralAttributionExpired(attribution, now)) {
        storage.removeItem(RESELLER_REFERRAL_STORAGE_KEY);
        return null;
    }

    return attribution;
};

export const writeReferralAttribution = ({
    code,
    sourceUrl = getCurrentHref(),
    capturedAt = new Date().toISOString(),
    landingPath = '',
} = {}) => {
    const normalizedCode = sanitizeResellerCode(code);
    const storage = getStorage();

    if (!normalizedCode || !storage) {
        return null;
    }

    // First-touch protection: preserve existing valid attribution
    const existing = readReferralAttribution();
    if (existing?.code) {
        return existing;
    }

    const attribution = {
        code: normalizedCode,
        capturedAt,
        sourceUrl,
        landingPath,
    };

    storage.setItem(RESELLER_REFERRAL_STORAGE_KEY, JSON.stringify(attribution));
    return attribution;
};

export const clearReferralAttribution = () => {
    const storage = getStorage();
    storage?.removeItem(RESELLER_REFERRAL_STORAGE_KEY);
};

export const extractReferralCodeFromSearch = (search = '') => {
    const params = new URLSearchParams(search || '');
    return sanitizeResellerCode(
        params.get('ref')
        || params.get('reseller')
        || params.get('partner')
        || params.get('r')
        || ''
    );
};

export const captureReferralFromLocation = ({
    search = typeof window !== 'undefined' ? window.location.search : '',
    sourceUrl = getCurrentHref(),
    landingPath = typeof window !== 'undefined' ? window.location.pathname : '',
} = {}) => {
    const code = extractReferralCodeFromSearch(search);
    if (!code) {
        return readReferralAttribution();
    }

    return writeReferralAttribution({
        code,
        sourceUrl,
        landingPath,
    });
};

export const buildReferralCheckoutMetadata = ({ now = Date.now() } = {}) => {
    const attribution = readReferralAttribution({ now });
    if (!attribution) {
        return null;
    }

    return {
        code: attribution.code,
        capturedAt: attribution.capturedAt,
        sourceUrl: attribution.sourceUrl,
        landingPath: attribution.landingPath,
        ttlDays: RESELLER_REFERRAL_TTL_DAYS,
    };
};
